import React, { useEffect, useMemo, useState } from "react";
import { accountsApi } from "../api";
import { useI18n } from "../i18n";
import { usePermissions } from "../contexts/PermissionContext";
import PermissionAction from "./PermissionAction";
import OrderModal from "./OrderModal";
import ExpenseModal from "./ExpenseModal";
import TransferModal from "./TransferModal";
import AccountCreateModal from "./AccountCreateModal";
import SubscriptionBanner from "./SubscriptionBanner";
import { showToast, showConfirm } from "../utils/toast";

export default function Accounts({ user, subStatus, onSubscribe }) {
  const { t } = useI18n();
  const { hasPermission } = usePermissions();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [openingBalance, setOpeningBalance] = useState("");
  const [creating, setCreating] = useState(false);

  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [transfers, setTransfers] = useState([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);

  const [selected, setSelected] = useState("");
  const [ledger, setLedger] = useState([]);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [showEditOb, setShowEditOb] = useState(false);
  const [editObEntryId, setEditObEntryId] = useState("");
  const [editObAmount, setEditObAmount] = useState("");
  const [editObDate, setEditObDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const accs = await accountsApi.list();
      setAccounts(accs || []);
      if (!selected) {
        const first = (accs && accs[0]) ? accs[0].accountId : "";
        setSelected(first);
      }
    } catch (e) {
      console.error("Failed to load accounts", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadTransfers = async () => {
    setLoadingTransfers(true);
    try {
      const list = await accountsApi.listTransfers(100);
      setTransfers(list || []);
    } catch (e) {
      console.error('Failed to load transfers', e);
    } finally { setLoadingTransfers(false); }
  };

  useEffect(() => { loadTransfers(); }, []);

  useEffect(() => {
    if (!selected) { setLedger([]); return; }
    (async () => {
      setLoadingLedger(true);
      try {
        const entries = await accountsApi.ledger(selected, { limit: 50 });
        setLedger(entries || []);
      } catch (e) {
        console.error("Failed to load ledger", e);
      } finally {
        setLoadingLedger(false);
      }
    })();
  }, [selected]);

  const handleCreate = async () => {
    if (!name) return;
    try {
      setCreating(true);
      await accountsApi.create({ name: name.trim(), openingBalance: parseFloat(openingBalance || 0) });
      setName("");
      setOpeningBalance("");
      await load();
    } catch (e) {
      console.error("Failed to create account", e);
    } finally {
      setCreating(false);
    }
  };

  const handleTransfer = async () => {
    if (!fromId || !toId || !amount || fromId === toId) return;
    try {
      setTransferring(true);
      await accountsApi.transfer({ fromAccountId: fromId, toAccountId: toId, amount: parseFloat(amount || 0), note });
      setAmount(""); setNote("");
      await load();
      await loadTransfers();
      if (selected === fromId || selected === toId) {
        const entries = await accountsApi.ledger(selected, { limit: 50 });
        setLedger(entries || []);
      }
    } catch (e) {
      console.error("Failed to transfer", e);
    } finally {
      setTransferring(false);
    }
  };

  const handleDeleteLedger = async (e) => {
    const confirmed = await showConfirm({
      title: 'Delete transaction?',
      message: 'Delete this transaction?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      destructive: true,
      variant: 'warning',
    });
    if (!confirmed) return;
    try {
      if (e.source === 'transfer' && e.refId) {
        await accountsApi.deleteTransfer(e.refId);
        await loadTransfers();
      } else {
        await accountsApi.deleteLedgerEntry(e.entryId);
      }
      if (selected) {
        const entries = await accountsApi.ledger(selected, { limit: 50 });
        setLedger(entries || []);
      }
    } catch (err) {
      console.error('Failed to delete transaction', err);
      showToast({ message: String(err?.message || err), variant: 'error' });
    }
  };

  const handleReconcile = async (accountId) => {
    try {
      await accountsApi.reconcile(accountId);
      await load();
    } catch (e) {
      console.error("Failed to reconcile", e);
    }
  };

  const sel = useMemo(() => (accounts || []).find(a => a.accountId === selected), [accounts, selected]);
  const totalBalance = useMemo(() => {
    return (accounts || []).reduce((sum, a) => sum + Number(a.balance || 0), 0);
  }, [accounts]);

  return (
    <div className="min-h-screen bg-white text-gray-900 p-4 font-sans">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">{t('acct.title')}</h1>
        <PermissionAction actionKey="money_accounts:create">
          <button 
            className="text-sm bg-green-600 text-white px-3 py-2 rounded-full shadow" 
            onClick={()=> {
              // Check if subscription is inactive
              if (subStatus?.valid === false) {
                onSubscribe?.();
                return;
              }
              // Check if trial user has exceeded quota
              if (subStatus?.plan === 'trial' && subStatus?.quotaInfo?.quotaExceeded) {
                onSubscribe?.();
                return;
              }
              setShowCreateAccount(true);
            }}
          >
            + {t('acct.create.create')}
          </button>
        </PermissionAction>
      </div>

      {/* Helpful Description */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">How Money Accounts Work</h3>
            <div className="text-sm text-gray-700 space-y-2">
              <p>
                <strong>Track Cash Flow:</strong> Create money accounts like "Cash", "Bank Account", or "Digital Wallet" to track where your money comes from and where it goes.
              </p>
              <p>
                <strong>Automatic Transaction Tracking:</strong> When you create orders and expenses, tag them to a money account. The system automatically reflects these transactions in your account balance.
              </p>
              <p>
                <strong>Opening Balance:</strong> Set an opening balance when creating accounts to start with your current cash/bank balance. This helps you track the complete financial picture.
              </p>
            </div>
          </div>
        </div>
      </div>

      <SubscriptionBanner 
        user={user} 
        subStatus={subStatus} 
        onSubscribe={onSubscribe}
        showCreateButton={true}
        quotaInfo={subStatus?.quotaInfo}
      />

      {/* Total Balance card */}
      <div className="bg-green-50 border border-green-100 rounded-2xl p-4 shadow-sm mb-4">
        <div className="text-xs text-gray-600">{t('acct.totalBalance')}</div>
        <div className="mt-1 text-2xl font-bold text-gray-900">₹{Number(totalBalance).toFixed(0)}</div>
      </div>

      {/* Accounts list */}
      <div className="bg-white border border-green-100 rounded-2xl p-4 shadow-sm mb-4 h-[420px] overflow-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold">{t('acct.list.title')}</div>
          <PermissionAction actionKey="money_accounts:edit">
            <button className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-full shadow" onClick={()=>{ setEditingTransfer({ fromAccountId: '', toAccountId: '', amount: '', note: '' }); setShowTransferModal(true); }}>Transfer Balance</button>
          </PermissionAction>
        </div>
        {loading ? (
          <div className="text-xs text-gray-500">{t('acct.loading')}</div>
        ) : (
          <div className="space-y-2">
            {(accounts || []).map((a) => (
              <div key={a.accountId} className={`p-3 rounded-xl border ${selected === a.accountId ? 'border-green-300 bg-green-50' : 'border-green-100 bg-white'} flex items-center justify-between cursor-pointer`} onClick={() => setSelected(a.accountId)}>
                <div className="min-w-0">
                  <div className="font-medium text-gray-800 truncate">{a.name}</div>
                  <div className="text-xs text-gray-500 truncate">{t('acct.balance')}: ₹{Number(a.balance || 0).toFixed(0)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="text-xs bg-white text-green-700 border border-green-200 px-3 py-1.5 rounded-full" onClick={(e) => { e.stopPropagation(); handleReconcile(a.accountId); }}>{t('acct.refresh') || 'Refresh'}</button>
                </div>
              </div>
            ))}
            {(accounts || []).length === 0 && <div className="text-xs text-gray-400">{t('acct.list.empty')}</div>}
          </div>
        )}
      </div>

      {/* Ledger */}
      <div className="bg-white border border-green-100 rounded-2xl p-4 shadow-sm mb-4 h-[480px] overflow-auto">
        <div className="text-sm font-semibold mb-3">
          {t('acct.ledger.title')}
          {sel ? <span className="ml-2 text-xs text-gray-600">({sel.name})</span> : null}
          <span className="ml-2 text-[11px] text-gray-500">(<button className="underline" onClick={()=> selected && handleReconcile(selected)}>{t('acct.refresh') || 'Refresh'}</button>)</span>
        </div>
        {!selected ? (
          <div className="text-xs text-gray-500">{t('acct.ledger.select')}</div>
        ) : loadingLedger ? (
          <div className="text-xs text-gray-500">{t('acct.loading')}</div>
        ) : ledger.length === 0 ? (
          <div className="text-xs text-gray-500">{t('acct.ledger.empty')}</div>
        ) : (
          <div className="divide-y">
            {ledger.map((e) => (
              <div key={e.entryId} className="py-2 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${e.kind === 'credit' || e.kind === 'transfer_in' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-green-100 text-gray-600'}`}>
                    {e.kind === 'credit' || e.kind === 'transfer_in' ? t('pl.ledger.credit') : t('pl.ledger.debit')}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{e.label || e.source}</div>
                    <div className="text-[11px] text-gray-500 truncate">{new Date(e.date?._seconds ? e.date._seconds*1000 : e.date).toLocaleDateString()} {e.note ? `· ${e.note}` : ''}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`text-sm font-semibold ${e.kind === 'credit' || e.kind === 'transfer_in' ? 'text-green-700' : 'text-gray-700'}`}>₹{Number(e.amount || 0).toFixed(0)}</div>
                  <PermissionAction actionKey="money_accounts:edit">
                    <button className="text-xs text-green-700 underline" onClick={()=>{
                      if (e.source === 'orders' && e.refId) { setEditingOrderId(e.refId); setShowOrderModal(true); }
                      else if (e.source === 'expenses' && e.refId) { setEditingExpenseId(e.refId); setShowExpenseModal(true); }
                      else if (e.source === 'transfer') { setEditingTransfer({ transferId: e.refId, amount: e.amount, note: e.note, fromAccountId: (e.kind==='transfer_out'? e.money_account_id : ''), toAccountId: (e.kind==='transfer_in'? e.money_account_id : '') }); setShowTransferModal(true); }
                      else if (e.source === 'manual' && (e.label === 'Opening Balance')) {
                        setEditObEntryId(e.entryId);
                        setEditObAmount(String(e.amount || ''));
                        const dt = new Date(e.date?._seconds ? e.date._seconds*1000 : e.date || Date.now());
                        setEditObDate(dt.toISOString().split('T')[0]);
                        setShowEditOb(true);
                      }
                    }}>Edit</button>
                  </PermissionAction>
                  <PermissionAction actionKey="money_accounts:delete">
                    <button className="text-xs text-red-600 underline" onClick={()=>handleDeleteLedger(e)}>Delete</button>
                  </PermissionAction>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <OrderModal open={!!showOrderModal} orderId={editingOrderId} onClose={async (changed)=>{ setShowOrderModal(false); setEditingOrderId(null); if (changed && selected) { const entries = await accountsApi.ledger(selected, { limit: 50 }); setLedger(entries || []); } }} />
      <ExpenseModal open={!!showExpenseModal} expenseId={editingExpenseId} onClose={async (changed)=>{ setShowExpenseModal(false); setEditingExpenseId(null); if (changed && selected) { const entries = await accountsApi.ledger(selected, { limit: 50 }); setLedger(entries || []); } }} />
      <TransferModal open={!!showTransferModal} transfer={editingTransfer} onClose={async (changed)=>{ setShowTransferModal(false); setEditingTransfer(null); if (changed && selected) { const entries = await accountsApi.ledger(selected, { limit: 50 }); setLedger(entries || []); await loadTransfers(); } }} />

      {/* Transfer section removed per request; use top Transfer Balance button */}
      <AccountCreateModal open={!!showCreateAccount} onClose={async (changed)=>{ setShowCreateAccount(false); if (changed) await load(); }} />

      {/* Opening Balance Edit Modal */}
      {showEditOb && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-green-900/10">
          <div className="w-full sm:w-[420px] bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-green-100">
            <div className="p-4 border-b border-green-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-800">Edit Opening Balance</div>
              <button className="text-sm text-gray-500" onClick={()=>setShowEditOb(false)}>✕</button>
            </div>
            <div className="p-4 grid grid-cols-1 gap-2">
              <input className="border rounded px-3 py-2" type="number" placeholder="Amount" value={editObAmount} onChange={(e)=>setEditObAmount(e.target.value)} />
              <input className="border rounded px-3 py-2" type="date" value={editObDate} onChange={(e)=>setEditObDate(e.target.value)} />
            </div>
            <div className="p-4 flex gap-2">
              <button className="flex-1 bg-green-600 text-white rounded-xl py-2 text-sm shadow" onClick={async ()=>{
                try {
                  const amount = parseFloat(editObAmount || '0');
                  await accountsApi.updateLedgerEntry(editObEntryId, { amount, date: editObDate });
                  if (selected) { try { await accountsApi.reconcile(selected); } catch {} }
                  if (selected) {
                    const entries = await accountsApi.ledger(selected, { limit: 50 });
                    setLedger(entries || []);
                  }
                  await load();
                  setShowEditOb(false);
                } catch (err) {
                  showToast({ message: String(err?.message || err), variant: 'error' });
                }
              }}>Save</button>
              <button className="flex-1 bg-white border border-green-200 text-green-700 rounded-xl py-2 text-sm" onClick={()=>setShowEditOb(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



